import React, { useState, useEffect, useCallback } from "react";
import styles from "./ScheduleForm.module.scss";
import { ProjectInfoPreset } from "../../models/PresetItem";

interface ScheduleFormProps {
  selectedItemId: string;
  scheduleData: any;
  onScheduleDataChange: (data: any) => void;
  selectedPresets?: {
    projectInfo?: {
      data: ProjectInfoPreset;
    };
  };
}

interface ScheduleRow {
  workTypeName: string; // 공종명
  percentage: number; // 보할 (%)
  ratios: number[]; // 보할 분배값 (6개월 배열)
  selectedPeriods: boolean[]; // 선택된 구간 (UI 표시용)
  totalAmount: string; // 합계금액
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  selectedItemId,
  scheduleData,
  onScheduleDataChange,
  selectedPresets,
}) => {
  // 기본 2개 행 초기화
  const [rows, setRows] = useState<ScheduleRow[]>([
    {
      workTypeName: "",
      percentage: 0,
      ratios: [0, 0, 0, 0, 0, 0],
      selectedPeriods: [false, false, false, false, false, false],
      totalAmount: "",
    },
    {
      workTypeName: "",
      percentage: 0,
      ratios: [0, 0, 0, 0, 0, 0],
      selectedPeriods: [false, false, false, false, false, false],
      totalAmount: "",
    },
  ]);

  // 컴포넌트 마운트 시 초기 데이터 전달
  useEffect(() => {
    onScheduleDataChange({
      rows: rows,
      totalWorkDays: 0,
      startDate: "",
      completionDate: "",
      totalAmount: 0,
      totalPercentage: 0,
      workPeriodDistribution: [0, 0, 0, 0, 0, 0], // 초기값
      subHeaderDates: [5, 10, 15, 20, 25, 31], // 기본값
    });
  }, []); // 컴포넌트 마운트 시에만 실행

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [totalWorkDays, setTotalWorkDays] = useState<number>(0);
  const [contractAmount, setContractAmount] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>("");
  const [completionDate, setCompletionDate] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");

  // 드래그 관련 상태
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [dragStartCell, setDragStartCell] = useState<number | null>(null);
  const [dragEndCell, setDragEndCell] = useState<number | null>(null);
  const [dragRowIndex, setDragRowIndex] = useState<number | null>(null);
  const [dragAction, setDragAction] = useState<
    "activate" | "deactivate" | null
  >(null); // 드래그 액션 타입

  // 호버 관련 상태
  const [hoveredCell, setHoveredCell] = useState<{
    rowIndex: number;
    cellIndex: number;
  } | null>(null);

  // totalWorkDays 변화 추적 및 셀 초기화
  useEffect(() => {
    // 공사기간이 변경되면 모든 행의 선택된 셀을 초기화
    if (totalWorkDays > 0) {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          ratios: [0, 0, 0, 0, 0, 0],
          selectedPeriods: [false, false, false, false, false, false],
        }))
      );
    }
  }, [totalWorkDays]);

  // 총 보할 계산
  const getTotalPercentage = () => {
    return rows.reduce((sum, row) => sum + row.percentage, 0);
  };

  // 전체 합계금액 계산
  const getTotalAmount = () => {
    return rows.reduce((sum, row) => {
      const amount = parseFloat(row.totalAmount.replace(/[^0-9]/g, "")) || 0;
      return sum + amount;
    }, 0);
  };

  // 프리셋 데이터에서 초기값 설정 (한 번만)
  useEffect(() => {
    if (selectedPresets?.projectInfo?.data && !startDate && !completionDate) {
      const projectData = selectedPresets.projectInfo.data;

      // 한국어 날짜 형식을 ISO 형식으로 변환하는 함수
      const parseKoreanDate = (koreanDateStr: string): string => {
        try {
          const match = koreanDateStr.match(
            /(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일/
          );
          if (match) {
            const year = match[1];
            const month = match[2].padStart(2, "0");
            const day = match[3].padStart(2, "0");
            return `${year}-${month}-${day}`;
          }
          return "";
        } catch (error) {
          console.error("날짜 파싱 오류:", error);
          return "";
        }
      };

      if (projectData.startDate) {
        setStartDate(parseKoreanDate(projectData.startDate));
      }
      if (projectData.completionDate) {
        setCompletionDate(parseKoreanDate(projectData.completionDate));
      }
    }
  }, [selectedPresets, startDate, completionDate]);

  // 날짜 변경 시 공사기간 자동 계산
  useEffect(() => {
    if (startDate && completionDate) {
      const start = new Date(startDate);
      const completion = new Date(completionDate);

      if (!isNaN(start.getTime()) && !isNaN(completion.getTime())) {
        const diffTime = completion.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setTotalWorkDays(diffDays > 0 ? diffDays : 0);
      } else {
        setTotalWorkDays(0);
      }
    } else {
      setTotalWorkDays(0);
    }
  }, [startDate, completionDate]);

  // 공사기간을 6개월로 균등 분배
  const calculateWorkPeriodDistribution = (totalDays: number): number[] => {
    const baseAmount = Math.floor(totalDays / 6);
    const remainder = totalDays % 6;
    const distribution = new Array(6).fill(baseAmount);

    // 나머지를 마지막 셀에만 몰아서 배분
    distribution[5] += remainder;

    return distribution;
  };

  // 서브 헤더 날짜 계산 (각 구간의 끝 날짜)
  const calculateSubHeaderDates = (totalDays: number): number[] => {
    if (totalDays === 0) return [5, 10, 15, 20, 25, 31]; // 기본값

    const distribution = calculateWorkPeriodDistribution(totalDays);
    const subHeaderDates: number[] = [];
    let cumulative = 0;

    for (let i = 0; i < 6; i++) {
      cumulative += distribution[i];
      subHeaderDates.push(cumulative);
    }

    return subHeaderDates;
  };

  // 행 추가
  const addRow = () => {
    const newRow: ScheduleRow = {
      workTypeName: "",
      percentage: 0,
      ratios: [0, 0, 0, 0, 0, 0],
      selectedPeriods: [false, false, false, false, false, false],
      totalAmount: "",
    };
    setRows([...rows, newRow]);
  };

  // 행 삭제
  const removeRow = (index: number) => {
    if (rows.length > 1) {
      const filteredRows = rows.filter((_, i) => i !== index);

      // 전체 합계금액 재계산
      const totalSum = filteredRows.reduce((sum, row) => {
        const amount = parseFloat(row.totalAmount.replace(/[^0-9]/g, "")) || 0;
        return sum + amount;
      }, 0);

      // 각 행의 보할 재계산 및 선택된 구간에 재분배
      const updatedRows = filteredRows.map((row) => {
        const amount = parseFloat(row.totalAmount.replace(/[^0-9]/g, "")) || 0;
        const percentage = totalSum > 0 ? (amount / totalSum) * 100 : 0;
        const roundedPercentage = Math.round(percentage);

        // 새로운 보할을 선택된 구간에 재분배
        const redistributedRatios = distributePercentageToSelectedPeriods(
          roundedPercentage,
          row.selectedPeriods
        );

        return {
          ...row,
          percentage: roundedPercentage,
          ratios: redistributedRatios,
        };
      });

      setRows(updatedRows);
    }
  };

  // 공종명 변경
  const updateWorkType = (index: number, value: string) => {
    setRows(
      rows.map((row, i) =>
        i === index ? { ...row, workTypeName: value } : row
      )
    );
  };

  // 합계금액 변경 및 보할 자동 계산
  const updateTotalAmount = (index: number, value: string) => {
    // 숫자만 추출
    const numericValue = parseFloat(value.replace(/[^0-9]/g, "")) || 0;
    const formattedValue = numericValue ? numericValue.toLocaleString() : "";

    // 먼저 해당 행의 금액을 업데이트
    const updatedRows = rows.map((row, i) =>
      i === index
        ? {
            ...row,
            totalAmount: formattedValue,
          }
        : row
    );

    // 전체 합계금액 계산
    const totalSum = updatedRows.reduce((sum, row) => {
      const amount = parseFloat(row.totalAmount.replace(/[^0-9]/g, "")) || 0;
      return sum + amount;
    }, 0);

    // 각 행의 보할 재계산 및 선택된 구간에 재분배
    const finalRows = updatedRows.map((row) => {
      const amount = parseFloat(row.totalAmount.replace(/[^0-9]/g, "")) || 0;
      const percentage = totalSum > 0 ? (amount / totalSum) * 100 : 0;
      const roundedPercentage = Math.round(percentage);

      // 새로운 보할을 선택된 구간에 재분배
      const redistributedRatios = distributePercentageToSelectedPeriods(
        roundedPercentage,
        row.selectedPeriods
      );

      return {
        ...row,
        percentage: roundedPercentage,
        ratios: redistributedRatios,
      };
    });

    setRows(finalRows);
  };

  // 마우스 다운 (드래그 준비)
  const handleMouseDown = (rowIndex: number, cellIndex: number) => {
    setIsMouseDown(true);
    setDragStartCell(cellIndex);
    setDragEndCell(cellIndex);
    setDragRowIndex(rowIndex);

    // 시작 셀의 상태에 따라 드래그 액션 결정
    const startCellActive = rows[rowIndex]?.selectedPeriods[cellIndex] || false;
    setDragAction(startCellActive ? "deactivate" : "activate");
  };

  // 드래그 중 셀 진입 & 호버 처리
  const handleMouseEnter = (rowIndex: number, cellIndex: number) => {
    // 호버 상태 설정
    setHoveredCell({ rowIndex, cellIndex });

    // 마우스가 눌린 상태에서 다른 셀로 이동하면 드래그 모드 시작
    if (
      isMouseDown &&
      dragRowIndex === rowIndex &&
      dragStartCell !== cellIndex
    ) {
      if (!isDragging) {
        setIsDragging(true);
      }
      setDragEndCell(cellIndex);
    }
  };

  // 마우스 탈출 처리
  const handleMouseLeave = () => {
    setHoveredCell(null);
  };

  // 마우스 업 (드래그 종료 또는 클릭 완료)
  const handleMouseUp = useCallback(() => {
    // 실제 드래그가 발생했을 때만 드래그 처리
    if (
      isDragging &&
      dragStartCell !== null &&
      dragEndCell !== null &&
      dragRowIndex !== null &&
      dragAction !== null
    ) {
      const startIndex = Math.min(dragStartCell, dragEndCell);
      const endIndex = Math.max(dragStartCell, dragEndCell);

      setRows((prevRows) =>
        prevRows.map((row, i) => {
          if (i === dragRowIndex) {
            const newSelectedPeriods = [...row.selectedPeriods];

            // 드래그 액션에 따라 활성화/비활성화
            for (let j = startIndex; j <= endIndex; j++) {
              newSelectedPeriods[j] = dragAction === "activate";
            }

            // 보할을 선택된 구간에 분배
            const distributedPercentages =
              distributePercentageToSelectedPeriods(
                row.percentage,
                newSelectedPeriods
              );

            return {
              ...row,
              selectedPeriods: newSelectedPeriods,
              ratios: distributedPercentages,
            };
          }
          return row;
        })
      );
    }

    // 모든 마우스 상태 초기화
    setIsMouseDown(false);
    setIsDragging(false);
    setDragStartCell(null);
    setDragEndCell(null);
    setDragRowIndex(null);
    setDragAction(null);
  }, [isDragging, dragStartCell, dragEndCell, dragRowIndex, dragAction]);

  // 전역 마우스 이벤트 처리
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isMouseDown || isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isMouseDown, isDragging, handleMouseUp]);

  // 개별 공사기간 셀 클릭 (드래그가 발생하지 않았을 때만)
  const handleWorkPeriodCellClick = (rowIndex: number, cellIndex: number) => {
    // 드래그가 발생했으면 클릭 무시
    if (isDragging) return;

    setRows(
      rows.map((row, i) => {
        if (i === rowIndex) {
          const newSelectedPeriods = [...row.selectedPeriods];
          // 현재 셀이 선택되어 있으면 해제, 선택 안되어 있으면 선택
          newSelectedPeriods[cellIndex] = !newSelectedPeriods[cellIndex];

          // 보할을 선택된 구간에 분배
          const distributedPercentages = distributePercentageToSelectedPeriods(
            row.percentage,
            newSelectedPeriods
          );

          return {
            ...row,
            selectedPeriods: newSelectedPeriods,
            ratios: distributedPercentages,
          };
        }
        return row;
      })
    );
  };

  // 착공일 변경 핸들러
  const handleStartDateChange = (value: string) => {
    setDateError("");

    // 착공일이 준공일보다 늦으면 준공일을 자동으로 조정
    if (value && completionDate && value > completionDate) {
      setCompletionDate(value);
      setDateError(
        "착공일이 준공일보다 늦어서 준공일을 자동으로 조정했습니다."
      );
      setTimeout(() => setDateError(""), 3000); // 3초 후 에러 메시지 제거
    }

    setStartDate(value);
  };

  // 준공일 변경 핸들러
  const handleCompletionDateChange = (value: string) => {
    setDateError("");

    // 준공일이 착공일보다 빠르면 착공일을 자동으로 조정
    if (value && startDate && value < startDate) {
      setStartDate(value);
      setDateError(
        "준공일이 착공일보다 빨라서 착공일을 자동으로 조정했습니다."
      );
      setTimeout(() => setDateError(""), 3000); // 3초 후 에러 메시지 제거
    }

    setCompletionDate(value);
  };

  // 드래그 중인 셀인지 판단
  const isDraggedCell = (rowIndex: number, cellIndex: number): boolean => {
    if (
      !isDragging ||
      dragRowIndex !== rowIndex ||
      dragStartCell === null ||
      dragEndCell === null
    ) {
      return false;
    }
    const startIndex = Math.min(dragStartCell, dragEndCell);
    const endIndex = Math.max(dragStartCell, dragEndCell);
    return cellIndex >= startIndex && cellIndex <= endIndex;
  };

  // 보할을 선택된 구간에 분배하는 함수
  const distributePercentageToSelectedPeriods = (
    percentage: number,
    selectedPeriods: boolean[]
  ): number[] => {
    // 선택된 구간의 개수 계산
    const selectedCount = selectedPeriods.filter(Boolean).length;

    if (selectedCount === 0) {
      return [0, 0, 0, 0, 0, 0];
    }

    // 기본 배분값 계산
    const baseAmount = Math.floor(percentage / selectedCount);
    const remainder = percentage % selectedCount;

    // 결과 배열 초기화
    const distributedPercentages = [0, 0, 0, 0, 0, 0];
    let selectedIndex = 0;

    // 선택된 구간에만 배분
    for (let i = 0; i < 6; i++) {
      if (selectedPeriods[i]) {
        distributedPercentages[i] = baseAmount;
        selectedIndex++;

        // 마지막 선택된 구간에 나머지 추가
        if (selectedIndex === selectedCount) {
          distributedPercentages[i] += remainder;
        }
      }
    }

    return distributedPercentages;
  };

  // 데이터 변경 시 부모 컴포넌트에 전달
  useEffect(() => {
    // 공사기간 분배 계산
    const workPeriodDistribution =
      calculateWorkPeriodDistribution(totalWorkDays);
    const subHeaderDates = calculateSubHeaderDates(totalWorkDays);

    onScheduleDataChange({
      rows: rows,
      totalWorkDays,
      startDate,
      completionDate,
      totalAmount: getTotalAmount(),
      totalPercentage: getTotalPercentage(),
      workPeriodDistribution, // 각 구간별 일수 [30, 30, 30, 30, 30, 30]
      subHeaderDates, // 각 구간의 끝 날짜 [30, 60, 90, 120, 150, 180]
    });
  }, [rows, totalWorkDays, startDate, completionDate]); // onScheduleDataChange 제거

  return (
    <div
      className="card shadow-sm mb-4"
      style={{ border: "none", borderRadius: "16px" }}
    >
      <div
        className="card-header bg-white border-0 pt-4"
        style={{ borderRadius: "16px 16px 0 0" }}
      >
        <h5 className="card-title mb-0 d-flex align-items-center">
          <span className="material-symbols-outlined me-2 text-info">
            schedule
          </span>
          예정공정표
        </h5>
        <p className="text-muted mb-0 mt-2">
          공사 예정공정표 정보를 입력하세요.
        </p>
      </div>
      <div className="card-body">
        {/* 날짜 입력 필드 */}
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold text-dark mb-2">
              <span
                className="material-symbols-outlined me-2"
                style={{ fontSize: "18px" }}
              >
                calendar_today
              </span>
              착공일
            </label>
            <input
              type="date"
              className={`form-control ${dateError ? "border-warning" : ""}`}
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              style={{ borderRadius: "8px" }}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold text-dark mb-2">
              <span
                className="material-symbols-outlined me-2"
                style={{ fontSize: "18px" }}
              >
                event_available
              </span>
              준공일
            </label>
            <input
              type="date"
              className={`form-control ${dateError ? "border-warning" : ""}`}
              value={completionDate}
              onChange={(e) => handleCompletionDateChange(e.target.value)}
              style={{ borderRadius: "8px" }}
            />
          </div>
        </div>

        {/* 날짜 에러 메시지 */}
        {dateError && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <span className="material-symbols-outlined me-2">warning</span>
                {dateError}
              </div>
            </div>
          </div>
        )}

        {/* 공사 정보 표시 */}
        <div className="row mb-3">
          <div className="col-md-6">
            <small className="text-muted">전체 합계금액: </small>
            <span className="fw-semibold">
              {getTotalAmount().toLocaleString()}원
            </span>
          </div>
          <div className="col-md-6">
            <small className="text-muted">총 공사기간: </small>
            <span className="fw-semibold">{totalWorkDays}일</span>
            {startDate && completionDate && (
              <small className="text-muted d-block">
                ({startDate} ~ {completionDate})
              </small>
            )}
          </div>
        </div>

        {/* 예정공정표 테이블 */}
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th style={{ width: "22%", padding: "8px" }}>공종명</th>
                <th style={{ width: "13%", padding: "8px" }}>보할(%)</th>
                <th colSpan={6} style={{ width: "42%", padding: "8px" }}>
                  공사기간
                </th>
                <th style={{ width: "18%", padding: "8px" }}>합계금액</th>
                <th style={{ width: "5%", padding: "8px" }}>작업</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                {calculateSubHeaderDates(totalWorkDays).map((date, index) => (
                  <th
                    key={index}
                    style={{
                      width: "7%",
                      fontSize: "12px",
                      padding: "4px",
                      textAlign: "center",
                    }}
                  >
                    {date}
                  </th>
                ))}
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td style={{ padding: "8px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.workTypeName}
                      onChange={(e) => updateWorkType(rowIndex, e.target.value)}
                      placeholder="공종명 입력"
                    />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm text-center"
                      value={row.percentage}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                  </td>
                  {row.selectedPeriods.map((isSelected, index) => {
                    const isCurrentlyDragged = isDraggedCell(rowIndex, index);
                    const isActive = isSelected;
                    const isHovered =
                      hoveredCell?.rowIndex === rowIndex &&
                      hoveredCell?.cellIndex === index;

                    // 보할 비율 표시
                    const displayRatio = row.ratios[index] || 0;

                    return (
                      <td
                        key={index}
                        className="cursor-pointer position-relative"
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          minHeight: "50px",
                          backgroundColor: isCurrentlyDragged
                            ? dragAction === "activate"
                              ? "#e3f2fd" // 활성화 드래그 (파란색)
                              : "#ffebee" // 비활성화 드래그 (빨간색)
                            : isActive
                            ? "#f8f9fa"
                            : isHovered
                            ? "#f0f8ff"
                            : "transparent",
                          userSelect: "none",
                          transition:
                            "background-color 0.2s ease, border-color 0.2s ease",
                          border:
                            isHovered && !isActive && !isCurrentlyDragged
                              ? "1px solid #b3d9ff"
                              : "1px solid #dee2e6",
                        }}
                        onMouseDown={() => handleMouseDown(rowIndex, index)}
                        onMouseEnter={() => handleMouseEnter(rowIndex, index)}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onClick={() =>
                          handleWorkPeriodCellClick(rowIndex, index)
                        }
                        title={`${index + 1}구간: ${
                          isActive ? "활성화" : "비활성화"
                        } ${
                          isCurrentlyDragged
                            ? `• 드래그로 ${
                                dragAction === "activate"
                                  ? "활성화"
                                  : "비활성화"
                              } 예정`
                            : isHovered
                            ? "• 클릭하여 토글 또는 드래그하여 범위 선택"
                            : "(클릭 또는 드래그하여 선택)"
                        }`}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: isActive
                              ? "bold"
                              : isHovered
                              ? "500"
                              : "normal",
                            color: isCurrentlyDragged
                              ? dragAction === "activate"
                                ? "#1976d2" // 활성화 드래그 (파란색)
                                : "#d32f2f" // 비활성화 드래그 (빨간색)
                              : isActive
                              ? "#0d6efd"
                              : isHovered
                              ? "#495057"
                              : "#6c757d",
                            transition:
                              "color 0.2s ease, font-weight 0.2s ease",
                          }}
                        >
                          {displayRatio}%
                        </div>
                        {(isActive || isCurrentlyDragged || isHovered) && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "0",
                              right: "0",
                              height: "3px",
                              backgroundColor: isCurrentlyDragged
                                ? dragAction === "activate"
                                  ? "#1976d2" // 활성화 드래그 (파란색)
                                  : "#d32f2f" // 비활성화 드래그 (빨간색)
                                : isActive
                                ? "#0d6efd"
                                : isHovered
                                ? "#b3d9ff"
                                : "transparent",
                              borderRadius: "0 0 4px 4px",
                              transition: "background-color 0.2s ease",
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: "8px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={row.totalAmount}
                      onChange={(e) =>
                        updateTotalAmount(rowIndex, e.target.value)
                      }
                      placeholder="금액 입력"
                    />
                  </td>
                  <td style={{ padding: "4px" }}>
                    <div className="d-flex flex-column gap-1">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeRow(rowIndex)}
                        disabled={rows.length <= 1}
                        style={{ fontSize: "10px", padding: "2px 4px" }}
                        title="행 삭제"
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "14px" }}
                        >
                          delete
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 행 추가 버튼 */}
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addRow}
          >
            <span className="material-symbols-outlined me-2">add</span>행 추가
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleForm;
